import { Link } from '@inertiajs/react'
import { Box, Flex, Text, TextField } from '@radix-ui/themes'
import { Camera, MapPin, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import CardBrowser, { BrowseGroup, CardThumb, FacetChip, showFilters, toggled } from '@/shared/browse/CardBrowser'
import MediaSocials from './MediaSocials'
import { MediaFilters, NO_FILTERS, filtersActive, listingFacets, mediaMatches } from './mediaFilters'
import { EventsShowMedia } from '@/types/events_show'
import { useBrand } from '@/shared/brand/useBrand'

interface Props<T extends EventsShowMedia> {
  groups: BrowseGroup<T>[]
}

/**
 * The media at an event, laid out like the cars (CarBrowser): their logo as
 * the thumbnail, filtered by how they're listed (photography, drone) or
 * anything typed, opened in place for their links.
 */
export default function MediaBrowser<T extends EventsShowMedia>({ groups }: Props<T>) {
  const [filters, setFilters] = useState<MediaFilters>(NO_FILTERS)
  const allMedia = useMemo(() => groups.flatMap((g) => g.items), [groups])
  const listings = useMemo(() => listingFacets(allMedia), [allMedia])

  const filtered = groups.map((group) => ({ ...group, items: group.items.filter((m) => mediaMatches(m, filters)) }))

  return (
    <CardBrowser
      groups={filtered}
      total={allMedia.length}
      active={filtersActive(filters)}
      onClear={() => setFilters(NO_FILTERS)}
      noMatch="No media match."
      getKey={(m) => m.id}
      renderSummary={(m) => <MediaSummary media={m} />}
      renderDetail={(m) => <MediaDetail media={m} />}
      filters={
        showFilters(allMedia.length) && (
          <>
            {/* One listing is no choice at all. */}
            {listings.length > 1 && (
              <Flex gap="2" wrap="wrap">
                {listings.map(({ value, label, count }) => (
                  <FacetChip
                    key={value}
                    on={filters.listings.includes(value)}
                    onClick={() => setFilters({ ...filters, listings: toggled(filters.listings, value) })}
                  >
                    {label} {count}
                  </FacetChip>
                ))}
              </Flex>
            )}

            <TextField.Root
              placeholder="Name, handle, location…"
              value={filters.query}
              onChange={(e) => setFilters({ ...filters, query: e.target.value })}
            >
              <TextField.Slot>
                <Search size={14} />
              </TextField.Slot>
            </TextField.Root>
          </>
        )
      }
    />
  )
}

function MediaSummary({ media }: { media: EventsShowMedia }) {
  return (
    <Flex gap="3" align="center">
      <CardThumb src={media.logo_thumb_url} fallback={<Camera size={28} color="var(--gray-8)" />} />
      <Box style={{ minWidth: 0 }}>
        <Text as="div" weight="bold" truncate>
          {media.name}
        </Text>
        <Text as="div" size="2" color="gray" truncate>
          {media.listing_label}
          {media.instagram && ` · @${media.instagram.replace(/^@+/, '')}`}
        </Text>
      </Box>
    </Flex>
  )
}

function MediaDetail({ media }: { media: EventsShowMedia }) {
  // A media group's page isn't part of a branded site; their socials are.
  const brand = useBrand()

  return (
    <Flex direction="column" gap="3">
      {media.location && (
        <Flex align="center" gap="1">
          <MapPin size={14} color="var(--gray-9)" />
          <Text size="2" color="gray">
            {media.location}
          </Text>
        </Flex>
      )}
      <MediaSocials media={media} />
      {!brand && (
        <Flex justify="end">
          <Link href={`/media_groups/${media.participantable_id}`} className="text-accent9 text-sm">
            View profile
          </Link>
        </Flex>
      )}
    </Flex>
  )
}
